import type {
  NavigationItem,
  NavigationItemId,
  TestStatus,
} from "../navigation/navigation";
import { navigationItems, userCanAccessNav } from "../navigation/navigation";
import logoAja2Light from "../assets/logo.png";
import logoAja2Dark from "../assets/logodark.png";
import {
  ChevronRight24Regular,
  ChevronDown24Regular,
  Navigation24Regular,
  PanelLeft24Regular,
  PanelLeftExpand24Regular,
} from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useAuth } from "../auth/AuthContext";

interface NavigationRailProps {
  activeItem: NavigationItemId;
  onNavigate: (item: NavigationItemId) => void;
  /** Contagens "à espera de alguém" por item (ex.: faturas por validar) — só se mostram > 0. */
  badges?: Partial<Record<string, { count: number; label: string }>>;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileMenuOpen: boolean;
  onCloseMobileMenu: () => void;
}

// O número "à espera de alguém" ao lado de um item do menu; não aparece a zero. O rótulo vai em
// texto escondido (não em aria-label, que num <span> não é lido) e diz o que o número conta.
const visuallyHidden: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

function CountBadge({ badge, compact }: { badge?: { count: number; label: string }; compact?: boolean }) {
  if (!badge || badge.count <= 0) return null;
  const hidden = <span style={visuallyHidden}>{` ${badge.count} ${badge.label}`}</span>;
  if (compact) {
    // Menu recolhido: só uma marca sobre o ícone, para não se perder o aviso.
    return (
      <span
        style={{
          position: "absolute",
          top: 6,
          right: 10,
          width: 8,
          height: 8,
          borderRadius: 999,
          backgroundColor: "#f59e0b",
        }}
      >
        {hidden}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center px-1.5 text-xs"
      // Ao passar o rato, o número diz o que está a contar (a pergunta era "que ações temos de tomar?").
      title={`${badge.count} ${badge.label}`}
      style={{
        minWidth: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: "#f59e0b",
        color: "#431407",
        fontWeight: 600,
      }}
    >
      <span aria-hidden="true">{badge.count}</span>
      {hidden}
    </span>
  );
}

// Bolinha de estado de entrega ao lado de cada item do menu.
// Cores fixas (inline) porque o index.css é Tailwind pré-compilado e não traz
// as classes sólidas bg-green-500 / bg-amber-500 — só variantes /10.
const STATUS_COLOR: Record<TestStatus, string> = {
  tested: "#22c55e", // verde — já entregue
  pending: "#f59e0b", // âmbar — em validação com o cliente
};

const STATUS_LABEL: Record<TestStatus, string> = {
  tested: "Já entregue",
  pending: "Em validação com o cliente",
};

function StatusDot({ status }: { status: TestStatus }) {
  return (
    <span
      className="flex-shrink-0"
      style={{
        width: 10,
        height: 10,
        borderRadius: 9999,
        backgroundColor: STATUS_COLOR[status],
      }}
      role="img"
      aria-label={STATUS_LABEL[status]}
      title={STATUS_LABEL[status]}
    />
  );
}

export function NavigationRail({
  activeItem,
  onNavigate,
  isCollapsed,
  onToggleCollapse,
  isMobileMenuOpen,
  onCloseMobileMenu,
  badges,
}: NavigationRailProps) {
  const collapsedBadge = (id: string, childIds: string[]) => {
    const count = [id, ...childIds].reduce((total, key) => total + (badges?.[key]?.count ?? 0), 0);
    return count > 0 ? { count, label: "à espera nesta área" } : undefined;
  };

  const { user } = useAuth();
  const isAdmin = user?.roles?.includes("Administrator") ?? false;

  // O menu mostra o que o acesso EFETIVO (união das funcionalidades dos cargos) permite —
  // decisão única partilhada com os guards de rota (userCanAccessNav). O backend é a autoridade;
  // isto só evita mostrar portas fechadas. Já não se filtra por nome de papel.
  const canAccess = useCallback(
    (navId: string) => userCanAccessNav(isAdmin, user?.features, navId),
    [isAdmin, user?.features],
  );

  const visibleItems = useMemo(
    () =>
      navigationItems
        .map((item) => {
          if (!item.children) {
            return canAccess(item.id) ? item : null;
          }
          const children = item.children.filter((child) => canAccess(child.target));
          return { ...item, children };
        })
        .filter((item): item is NavigationItem => item !== null)
        .filter((item) => !item.children || item.children.length > 0),
    [canAccess],
  );

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    const activeGroup = navigationItems.find((item) =>
      item.children?.some((child) => child.target === activeItem),
    );
    if (activeGroup) {
      initial.add(activeGroup.id);
    }
    return initial;
  });
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Mantém o grupo da página atual aberto ao navegar (sem colar os outros).
  useEffect(() => {
    const activeGroup = navigationItems.find((item) =>
      item.children?.some((child) => child.target === activeItem),
    );
    if (!activeGroup) {
      return;
    }
    setExpandedSections((previous) => {
      if (previous.has(activeGroup.id)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(activeGroup.id);
      return next;
    });
  }, [activeItem]);

  // Detectar tema dark/light
  useEffect(() => {
    const checkTheme = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    // Verificar tema inicial
    checkTheme();

    // Observar mudanças no tema
    if (typeof MutationObserver === "undefined") return;

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const isItemActive = (item: NavigationItem): boolean => {
    if (item.id === activeItem) return true;
    if (item.children) {
      return item.children.some(
        (child) => child.target === activeItem,
      );
    }
    return false;
  };

  return (
    <nav
      className={`
        flex flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300
        md:relative md:translate-x-0
        fixed inset-y-0 left-0 z-50
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
      style={{ width: isCollapsed ? "60px" : "240px" }}
    >
      {/* Header with Toggle */}
      <div className="flex items-center justify-between h-12 px-4 border-b border-sidebar-border bg-card">
        {!isCollapsed && (
          <img 
            src={isDarkMode ? logoAja2Dark : logoAja2Light}
            alt="aja2 - Projectos & Construções" 
            className="h-8 object-contain"
          />
        )}
        <button
          onClick={onToggleCollapse}
          className="p-1.5 rounded hover:bg-sidebar-accent text-sidebar-foreground transition-colors hidden md:block"
          aria-label={
            isCollapsed
              ? "Expand navigation"
              : "Collapse navigation"
          }
        >
          {isCollapsed ? (
            <PanelLeftExpand24Regular className="w-6 h-6" />
          ) : (
            <PanelLeft24Regular className="w-6 h-6" />
          )}
        </button>
        <button
          onClick={onCloseMobileMenu}
          className="p-1.5 rounded hover:bg-sidebar-accent text-sidebar-foreground transition-colors md:hidden"
          aria-label="Close menu"
        >
          <Navigation24Regular className="w-6 h-6" />
        </button>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto py-2">
        {visibleItems.map((item) => {
          const ItemIcon = isItemActive(item) ? item.iconFilled : item.iconRegular;
          // Só itens navegáveis de topo (ex.: Dashboard) mostram bolinha;
          // os cabeçalhos de grupo nunca têm estado.
          const itemStatus = item.children ? undefined : item.status;

          return (
            <div key={item.id}>
              {/* Parent Item */}
              <button
                onClick={() => {
                  if (item.children) {
                    if (isCollapsed) {
                      // Rail colada: expande a rail e abre este grupo.
                      onToggleCollapse();
                      setExpandedSections((previous) =>
                        new Set(previous).add(item.id),
                      );
                    } else {
                      toggleSection(item.id);
                    }
                  } else {
                    onNavigate(item.id);
                  }
                }}
                className={`
                  w-full flex items-center gap-3 px-4 py-2 transition-colors min-h-[44px]
                  ${
                    isItemActive(item)
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                  }
                  ${isCollapsed ? "justify-center md:justify-center" : ""}
                `}
                title={isCollapsed ? item.label : undefined}
              >
                <span className="flex-shrink-0" style={{ position: "relative", display: "inline-flex" }}>
                  <ItemIcon className="w-6 h-6" />
                  {/* Recolhido, o número não cabe: fica uma marca no ícone (do item ou de um sub-item). */}
                  {isCollapsed && (
                    <CountBadge
                      compact
                      badge={collapsedBadge(item.id, item.children?.map((child) => child.id) ?? [])}
                    />
                  )}
                </span>

                {!isCollapsed && (
                  <>
                    <span className="flex-1 text-left">
                      {item.label}
                    </span>
                    <CountBadge badge={badges?.[item.id]} />
                    {itemStatus && <StatusDot status={itemStatus} />}
                    {item.children &&
                      (expandedSections.has(item.id) ? (
                        <ChevronDown24Regular className="w-5 h-5 flex-shrink-0" />
                      ) : (
                        <ChevronRight24Regular className="w-5 h-5 flex-shrink-0" />
                      ))}
                  </>
                )}
              </button>

              {/* Child Items */}
              {item.children &&
                !isCollapsed && (
                  /* A altura vem do conteúdo, não de um número fixo: com um limite em pixels, o
                     dia em que um grupo ganha mais um item passa a cortar os últimos em silêncio,
                     que foi o que aconteceu ao Financeiro. As linhas da grelha animam de 0 à
                     altura real, e num browser que não saiba animá-las abre de repente, que é
                     pior do que abrir devagar mas melhor do que esconder o que lá está. */
                  <div
                    className="overflow-hidden transition-all duration-200 ease-in-out"
                    style={{
                      display: "grid",
                      gridTemplateRows: expandedSections.has(item.id) ? "1fr" : "0fr",
                      opacity: expandedSections.has(item.id) ? 1 : 0,
                    }}
                  >
                    <div className="bg-sidebar/50" style={{ minHeight: 0, overflow: "hidden" }}>
                      {item.children.map((child) => (
                        <button
                          key={child.id}
                          onClick={() => onNavigate(child.target)}
                          className={`
                          w-full flex items-center gap-3 px-4 py-2 pl-12 transition-colors min-h-[44px]
                          ${
                            activeItem === child.target
                              ? "bg-sidebar-accent text-sidebar-accent-foreground"
                              : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                          }
                        `}
                        >
                          <span className="flex-1 text-left">
                            {child.label}
                          </span>
                          <CountBadge badge={badges?.[child.id]} />
                          {child.status && <StatusDot status={child.status} />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
